import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiX } from 'react-icons/fi';
import { useAuthStore } from '../store/useAuthStore';
import { useStore } from '../store/useStore';
import '../styles/Profile.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Profile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const isHydrating = useAuthStore((state) => state.isHydrating);
  const logout = useAuthStore((state) => state.logout);
  const setUser = useAuthStore((state) => state.setUser);
  const products = useStore((state) => state.products);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'orders');
  const [selectedOrderId, setSelectedOrderId] = useState(searchParams.get('id'));
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    type: 'home',
    street_address: '',
    city: '',
    postal_code: '',
    country: '',
    phone: '',
  });

  // Phone and account deletion states
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Redirect to login if not authenticated (but wait for hydration to complete)
  useEffect(() => {
    if (!isHydrating && (!user || !token)) {
      navigate('/login');
    }
  }, [isHydrating, user, token, navigate]);

  // Update tab and selectedOrderId when URL changes
  useEffect(() => {
    const tab = searchParams.get('tab') || 'orders';
    const id = searchParams.get('id');
    setActiveTab(tab);
    setSelectedOrderId(id);
  }, [searchParams]);

  // Helper function to format date with time
  const formatDateWithTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString();
    return `${dateStr} ${timeStr}`;
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/user`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Orders fetched from API:', data);
        setOrders(data);
      } else {
        console.error('Failed to fetch orders:', response.status);
        setOrders([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAddresses = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/addresses`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAddresses(data);
      } else {
        console.error('Failed to fetch addresses:', response.status);
        setAddresses([]);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddresses([]);
    }
  }, [token]);

  const fetchWishlist = useCallback(async () => {
    if (!API_BASE_URL || !token) {
      setWishlist([]);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/wishlists`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setWishlist(Array.isArray(data) ? data : []);
      } else {
        console.warn(`Wishlist fetch returned ${response.status}: ${response.statusText}`);
        setWishlist([]);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setWishlist([]);
    }
  }, [token]);

  // Fetch user data (orders, addresses, wishlist)
  useEffect(() => {
    if (user && token) {
      fetchOrders();
      fetchAddresses();
      fetchWishlist();
    }
  }, [user, token, fetchOrders, fetchAddresses, fetchWishlist]);

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newAddress),
      });

      if (response.ok) {
        setShowAddAddress(false);
        setNewAddress({
          type: 'home',
          street_address: '',
          city: '',
          postal_code: '',
          country: '',
          phone: '',
        });
        await fetchAddresses();
      }
    } catch (error) {
      console.error('Error adding address:', error);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/addresses/${addressId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        await fetchAddresses();
      }
    } catch (error) {
      console.error('Error deleting address:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUpdatePhone = async () => {
    if (!newPhoneNumber.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }

    // Basic phone validation
    const phoneRegex = /^[\d\s\-+()]{10,}$/;
    if (!phoneRegex.test(newPhoneNumber.replace(/\s/g, ''))) {
      setPhoneError('Please enter a valid phone number');
      return;
    }

    setPhoneError('');
    setPhoneLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/send-phone-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: newPhoneNumber }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code');
      }

      // Show verification dialog (simplified - you can create a separate component)
      const code = prompt('Enter the verification code sent to your phone:');
      if (!code) {
        setShowPhoneModal(false);
        setPhoneError('');
        return;
      }

      // Verify code
      const verifyResponse = await fetch(`${API_BASE_URL}/api/users/verify-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: newPhoneNumber,
          verificationCode: code.trim()
        }),
      });

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Verification failed');
      }

      // Update user in auth store
      if (verifyData.user) {
        setUser(verifyData.user);
      }

      setShowPhoneModal(false);
      setNewPhoneNumber('');
      setPhoneError('');
    } catch (err) {
      console.error('Error updating phone:', err);
      setPhoneError(err.message);
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleRequestDelete = async () => {
    setDeleteError('');
    setDeleteLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/request-deletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request account deletion');
      }

      // Show success and logout after a delay
      alert('Your account deletion request has been submitted. You will be logged out now. Your account will be deleted after 30 days.');
      setShowDeleteConfirmDialog(false);
      handleLogout();
    } catch (err) {
      console.error('Error requesting deletion:', err);
      setDeleteError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Sidebar */}
        <aside className="profile-sidebar">
          <div className="profile-header">
            <div className="profile-info">
              <h2>{user.name}</h2>
              <p>{user.email}</p>
              {user.phone && <p>{user.phone}</p>}
              <span className={`role-badge role-${user.role}`}>
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </span>
            </div>
          </div>

          <nav className="profile-nav">
            <button
              className={`profile-nav-btn ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              📦 Order History
            </button>
            <button
              className={`profile-nav-btn ${activeTab === 'addresses' ? 'active' : ''}`}
              onClick={() => setActiveTab('addresses')}
            >
              📍 Addresses
            </button>
            <button
              className={`profile-nav-btn ${activeTab === 'wishlist' ? 'active' : ''}`}
              onClick={() => setActiveTab('wishlist')}
            >
              ❤️ Wishlist
            </button>
            <button
              className={`profile-nav-btn ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              ⚙️ Account Settings
            </button>
          </nav>

          <button className="btn-logout" onClick={handleLogout}>
            Log Out
          </button>
        </aside>

        {/* Main Content */}
        <main className="profile-content">
          {/* Order History Tab */}
          {activeTab === 'orders' && !selectedOrderId && (
            <section className="profile-section">
              <h1>Order History</h1>

              {loading ? (
                <div className="loading">Loading your orders...</div>
              ) : orders.length === 0 ? (
                <div className="empty-state">
                  <p>No orders yet.</p>
                  <p>Start shopping to see your orders here!</p>
                  <button className="btn-shop" onClick={() => navigate('/products')}>
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="orders-list">
                  {orders.map((order) => (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div>
                          <h3>{order.order_number || `Order #${order.id}`}</h3>
                          <p className="order-id-small">ID: {order.id}</p>
                        </div>
                        <span className={`status-badge status-${order.status}`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </div>

                      <div className="order-details">
                        <div className="detail-row">
                          <span className="label">Order Date:</span>
                          <span className="value">
                            {formatDateWithTime(order.created_at)}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Total Amount:</span>
                          <span className="value price">${order.total.toFixed(2)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Status:</span>
                          <span className="value">{order.status}</span>
                        </div>
                      </div>

                      <button className="btn-view-order" onClick={() => {
                        setSelectedOrderId(order.id);
                        navigate(`/profile?tab=order&id=${order.id}`);
                      }}>
                        View Details
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Order Details Tab */}
          {activeTab === 'order' && selectedOrderId && (
            <section className="profile-section">
              <div className="order-detail-header">
                <button className="btn-back" onClick={() => {
                  setSelectedOrderId(null);
                  navigate('/profile?tab=orders');
                }}>
                  ← Back to Orders
                </button>
              </div>
              {loading ? (
                <div className="loading">Loading order details...</div>
              ) : (
                (() => {
                  const selectedOrder = orders.find(o => o.id === parseInt(selectedOrderId));

                  if (!selectedOrder) {
                    return (
                      <div className="empty-state">
                        <p>Order not found.</p>
                        <button onClick={() => {
                          setSelectedOrderId(null);
                          navigate('/profile?tab=orders');
                        }}>
                          Back to Orders
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div className="order-detail-view">
                      {/* Order Header */}
                      <div className="order-header-section">
                        <div className="header-top">
                          <div>
                            <h2>{selectedOrder.order_number || `Order #${selectedOrder.id}`}</h2>
                            <p className="order-id-note">For support inquiries, please reference this order number</p>
                          </div>
                          <span className={`status-badge status-${selectedOrder.status}`}>
                            {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                          </span>
                        </div>
                        <p className="order-date">{formatDateWithTime(selectedOrder.created_at)}</p>
                      </div>

                      <div className="order-content">
                        {/* Left Column: Items */}
                        <div className="order-items-section">
                          <h3>Order Items</h3>
                          {selectedOrder.items && Array.isArray(selectedOrder.items) && selectedOrder.items.length > 0 ? (
                            <div className="order-items-list">
                              {selectedOrder.items.map((item, idx) => (
                                <div key={idx} className="item-card">
                                  <div className="item-header">
                                    <h4>{item.product_name || 'Product'}</h4>
                                    <span className="item-total">${((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                                  </div>
                                  <div className="item-details-grid">
                                    <div className="item-detail">
                                      <span className="detail-label">Unit Price:</span>
                                      <span className="detail-value">${(item.unit_price || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="item-detail">
                                      <span className="detail-label">Quantity:</span>
                                      <span className="detail-value">{item.quantity || 0}</span>
                                    </div>
                                    <div className="item-detail">
                                      <span className="detail-label">Size:</span>
                                      <span className="detail-value">{item.size || 'N/A'}</span>
                                    </div>
                                    <div className="item-detail">
                                      <span className="detail-label">Color:</span>
                                      <span className="detail-value">{item.color || 'N/A'}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-items">
                              <p>No items in this order</p>
                            </div>
                          )}
                        </div>

                        {/* Right Column: Info */}
                        <div className="order-info-section">
                          {/* Shipping Address */}
                          <div className="info-card">
                            <h3>Shipping Address</h3>
                            <p className="address-text">
                              {selectedOrder.shipping_address || 'No shipping address provided'}
                            </p>
                          </div>

                          {/* Order Summary */}
                          <div className="info-card">
                            <h3>Order Summary</h3>
                            <div className="summary-details">
                              <div className="summary-item">
                                <span>Subtotal:</span>
                                <span>${parseFloat(selectedOrder.subtotal || 0).toFixed(2)}</span>
                              </div>
                              {selectedOrder.shipping > 0 && (
                                <div className="summary-item">
                                  <span>Shipping:</span>
                                  <span>${parseFloat(selectedOrder.shipping).toFixed(2)}</span>
                                </div>
                              )}
                              <div className="summary-item divider">
                                <span className="label-bold">Total:</span>
                                <span className="amount-bold">${parseFloat(selectedOrder.total || 0).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Order Status Info */}
                          <div className="info-card">
                            <h3>Status Information</h3>
                            <div className="status-info">
                              <p><strong>Current Status:</strong> {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}</p>
                              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                                {selectedOrder.status === 'pending' && 'Your order is awaiting processing.'}
                                {selectedOrder.status === 'processing' && 'Your order is being prepared for shipment.'}
                                {selectedOrder.status === 'shipped' && 'Your order has been shipped. Check your email for tracking information.'}
                                {selectedOrder.status === 'delivered' && 'Your order has been delivered. Thank you for your purchase!'}
                                {selectedOrder.status === 'cancelled' && 'This order has been cancelled.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </section>
          )}

          {/* Addresses Tab */}
          {activeTab === 'addresses' && (
            <section className="profile-section">
              <div className="section-header">
                <h1>Saved Addresses</h1>
                <button className="btn-add-address" onClick={() => setShowAddAddress(!showAddAddress)}>
                  <FiPlus /> Add Address
                </button>
              </div>

              {showAddAddress && (
                <form className="address-form" onSubmit={handleAddAddress}>
                  <div className="form-group">
                    <label>Address Type</label>
                    <select
                      value={newAddress.type}
                      onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}
                    >
                      <option value="home">Home</option>
                      <option value="work">Work</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      value={newAddress.street_address}
                      onChange={(e) => setNewAddress({ ...newAddress, street_address: e.target.value })}
                      placeholder="e.g., 123 Main Street"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        value={newAddress.city}
                        onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal Code *</label>
                      <input
                        type="text"
                        value={newAddress.postal_code}
                        onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                        placeholder="Postal Code"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Country *</label>
                    <input
                      type="text"
                      value={newAddress.country}
                      onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                      placeholder="Country"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={newAddress.phone}
                      onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                      placeholder="Phone number"
                    />
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-save">Save Address</button>
                    <button type="button" className="btn-cancel" onClick={() => setShowAddAddress(false)}>Cancel</button>
                  </div>
                </form>
              )}

              {addresses.length > 0 ? (
                <div className="addresses-list">
                  {addresses.map((address) => (
                    <div key={address.id} className="address-card">
                      <div className="address-header">
                        <h3>{address.type.charAt(0).toUpperCase() + address.type.slice(1)}</h3>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteAddress(address.id)}
                          title="Delete address"
                        >
                          <FiX />
                        </button>
                      </div>
                      <p>{address.street_address}</p>
                      <p>{address.city}, {address.postal_code}</p>
                      <p>{address.country}</p>
                      {address.phone && <p className="phone">📞 {address.phone}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No addresses saved yet.</p>
                  <p>Add an address for faster checkout!</p>
                </div>
              )}
            </section>
          )}

          {/* Wishlist Tab */}
          {activeTab === 'wishlist' && (
            <section className="profile-section">
              <h1>Your Wishlist</h1>
              {wishlist.length === 0 ? (
                <div className="empty-state">
                  <p>Your wishlist is empty.</p>
                  <p>Add products to your wishlist to save them for later!</p>
                  <button className="btn-shop" onClick={() => navigate('/products')}>
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="wishlist-grid">
                  {wishlist.map((item) => {
                    console.log('Rendering wishlist item:', item);
                    return (
                      <div key={item.id} className="wishlist-card">
                        <div className="wishlist-image">
                          <img src={products.find(p => p.id === item.product_id)?.image || 'https://via.placeholder.com/300'} alt={item.name} />
                        </div>
                        <div className="wishlist-info">
                          <h3>{item.name}</h3>
                          <p className="wishlist-category">{item.category}</p>
                          <p className="wishlist-price">${parseFloat(item.price).toFixed(2)}</p>
                          <div className="wishlist-actions">
                            <button className="btn-view" onClick={() => navigate(`/product/${item.product_id}`)}>
                              View Product
                            </button>
                            <button className="btn-remove-wishlist" onClick={() => {
                              fetch(`${API_BASE_URL}/api/wishlists/${item.product_id}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${token}` },
                              }).then(() => fetchWishlist());
                            }}>
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Account Settings Tab */}
          {activeTab === 'account' && (
            <section className="profile-section">
              <h1>Account Settings</h1>

              <div className="account-info">
                <div className="info-group">
                  <label>Full Name</label>
                  <p className="info-value">{user.name}</p>
                </div>

                <div className="info-group">
                  <label>Email Address</label>
                  <p className="info-value">{user.email}</p>
                </div>

                <div className="info-group">
                  <label>Account Type</label>
                  <p className="info-value">
                    {user.role === 'admin' ? 'Admin Account' : 'Customer Account'}
                  </p>
                </div>

                {user.role === 'admin' && (
                  <div className="admin-info">
                    <p className="admin-note">
                      You have admin privileges. Visit the <a href="/admin">Admin Dashboard</a> to manage products, orders, and discounts.
                    </p>
                  </div>
                )}
              </div>

              {/* Phone Number Section */}
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>📱 Phone Number</h2>
                <div className="phone-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div>
                      <p style={{ marginBottom: '0.25rem', fontWeight: '500' }}>
                        Current Phone: {user.phone ? user.phone : 'Not set'}
                      </p>
                      {user.phone_verified ? (
                        <p style={{ fontSize: '0.85rem', color: '#28a745' }}>✓ Verified</p>
                      ) : user.phone ? (
                        <p style={{ fontSize: '0.85rem', color: '#ff9800' }}>⚠ Not verified</p>
                      ) : (
                        <p style={{ fontSize: '0.85rem', color: '#999' }}>No phone number on file</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPhoneModal(true)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {user.phone ? 'Change Phone Number' : 'Add Phone Number'}
                  </button>
                </div>
              </div>

              {/* Account Deletion Section */}
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>⚠️ Danger Zone</h2>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  Request to permanently delete your account and all associated data
                </p>
                <button
                  onClick={() => setShowDeleteConfirmDialog(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Request Account Deletion
                </button>
              </div>

              {/* Phone Modal */}
              {showPhoneModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }} onClick={() => setShowPhoneModal(false)}>
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '2rem',
                    maxWidth: '400px',
                    width: '90%',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                  }} onClick={(e) => e.stopPropagation()}>
                    <h2 style={{ marginTop: 0 }}>Update Phone Number</h2>
                    {phoneError && <div style={{
                      background: '#f8d7da',
                      color: '#721c24',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      marginBottom: '1rem'
                    }}>{phoneError}</div>}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                        New Phone Number
                      </label>
                      <input
                        type="tel"
                        value={newPhoneNumber}
                        onChange={(e) => setNewPhoneNumber(e.target.value)}
                        placeholder="e.g., +1 (555) 123-4567"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.95rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        onClick={() => setShowPhoneModal(false)}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdatePhone}
                        disabled={phoneLoading}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {phoneLoading ? 'Updating...' : 'Update & Verify'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Account Confirmation Dialog */}
              {showDeleteConfirmDialog && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }} onClick={() => setShowDeleteConfirmDialog(false)}>
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '2rem',
                    maxWidth: '400px',
                    width: '90%',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                  }} onClick={(e) => e.stopPropagation()}>
                    <h2 style={{ marginTop: 0, color: '#dc3545' }}>Delete Account?</h2>
                    <p>
                      This action cannot be undone. Your account and all associated data (orders, wishlist, addresses) will be permanently deleted after a 30-day grace period.
                    </p>
                    <p style={{ color: '#999', fontSize: '0.9rem' }}>
                      You can cancel the deletion request within 30 days.
                    </p>
                    {deleteError && <div style={{
                      background: '#f8d7da',
                      color: '#721c24',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      marginBottom: '1rem'
                    }}>{deleteError}</div>}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        onClick={() => setShowDeleteConfirmDialog(false)}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRequestDelete}
                        disabled={deleteLoading}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {deleteLoading ? 'Processing...' : 'Request Deletion'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="account-actions">
                <button className="btn-logout" onClick={handleLogout}>
                  Log Out
                </button>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
